// Edição simultânea: cada página aberta tem um documento Yjs ligado ao
// servidor por WebSocket (as mudanças de todo mundo se juntam letra por
// letra) e guardado no computador (IndexedDB), para abrir na hora e continuar
// editando sem internet. A presença (quem está na página e onde está o
// cursor) vai pelo "awareness" do Yjs.

import * as Y from "yjs";
import { WebsocketProvider } from "y-websocket";
import { IndexeddbPersistence } from "y-indexeddb";
import { apiBase, apiToken } from "./api.js";
import { safeGet, safeSet } from "./dom.js";
import * as store from "./store.js";

const COLORS = ["#e03e3e", "#d9730d", "#dfab01", "#0f7b6c", "#0b6e99", "#6940a5", "#ad1a72", "#2e7d32"];

export function colorFor(id) {
  let h = 0;
  for (const c of String(id || "")) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return COLORS[h % COLORS.length];
}

export const liveEnabled = () => !!store.state.features?.live;

// O documento só pode ser editado depois de ter vindo do servidor ao menos
// uma vez (assim duas cópias nunca nascem separadas e se duplicam).
const syncedKey = (pageId) => `ordo.live.synced.${store.state.me?.id}.${pageId}`;

export function openLive(pageId) {
  const me = store.state.me;
  const doc = new Y.Doc();
  const local = new IndexeddbPersistence(`ordo.live.${me.id}.${pageId}`, doc);
  const base = apiBase().replace(/^http/i, "ws") + "/live";
  const provider = new WebsocketProvider(base, pageId, doc, {
    protocols: ["ordo-live", apiToken()],
    // Pede o estado ao servidor de tempos em tempos: mantém a conexão viva e
    // corrige qualquer mensagem perdida.
    resyncInterval: 20000,
    disableBc: true,
  });
  const user = { id: me.id, name: me.name || "Alguém", avatar: me.avatar || null, color: colorFor(me.id) };
  provider.awareness.setLocalStateField("user", user);

  let resolveReady;
  const ready = new Promise((r) => { resolveReady = r; });
  let isReady = false;
  const markReady = (from) => {
    if (isReady) return;
    isReady = true;
    resolveReady(from);
  };
  provider.on("sync", (synced) => {
    if (!synced) return;
    safeSet(syncedKey(pageId), 1);
    markReady("server");
  });
  local.whenSynced.then(() => {
    if (safeGet(syncedKey(pageId))) markReady("local");
  }).catch(() => {});

  const listeners = new Set();
  const status = { connected: false };
  provider.on("status", ({ status: s }) => {
    status.connected = s === "connected";
    for (const fn of listeners) fn();
  });
  provider.awareness.on("change", () => { for (const fn of listeners) fn(); });

  // Pessoas na página agora (uma por conta, mesmo com duas janelas abertas).
  const people = () => {
    const seen = new Map();
    for (const [clientId, st] of provider.awareness.getStates()) {
      if (!st?.user?.id) continue;
      const prev = seen.get(st.user.id);
      if (!prev || clientId < prev.clientId) seen.set(st.user.id, { ...st.user, clientId, self: clientId === doc.clientID });
    }
    return [...seen.values()];
  };

  // Um único app (entre os que podem editar) "lidera" ações automáticas no texto (por exemplo, colocar o
  // link das subpáginas novas), para que não sejam feitas duas vezes.
  const leader = () => {
    let min = doc.clientID;
    for (const [clientId, st] of provider.awareness.getStates()) if (st?.user && st.editor && clientId < min) min = clientId;
    return min === doc.clientID;
  };

  return {
    doc, provider, user, ready, status, people, leader,
    get isReady() { return isReady; },
    onChange(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    destroy() {
      listeners.clear();
      provider.awareness.setLocalState(null);
      provider.destroy();
      local.destroy().catch(() => {});
      doc.destroy();
    },
  };
}
